import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { groupsAPI, billsAPI } from '../services/api';
import { Group } from '../types';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Avatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { BillUploader } from '../components/features/BillUploader';
import { SplitModeSelector, SplitMode } from '../components/features/SplitModeSelector';
import { QuickAddMember, NewMember } from '../components/features/QuickAddMember';

interface LocalMember {
  id: string;
  name: string;
  isVegetarian: boolean;
  drinksAlcohol: boolean;
  isNew?: boolean;
}

const ScanBill: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'upload' | 'setup' | 'processing'>('upload');
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [splitMode, setSplitMode] = useState<SplitMode>('food_alcohol');
  const [members, setMembers] = useState<LocalMember[]>([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      const data = await groupsAPI.getAll();
      setGroups(data.groups);
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  };

  const loadGroupMembers = async (groupId: string) => {
    try {
      const data = await groupsAPI.getById(groupId);
      setMembers(
        data.members.map((m) => ({
          id: m.id,
          name: m.name,
          isVegetarian: m.is_vegetarian,
          drinksAlcohol: m.drinks_alcohol,
        }))
      );
    } catch (error) {
      console.error('Failed to load members:', error);
    }
  };

  const handleGroupChange = (groupId: string) => {
    setSelectedGroup(groupId);
    if (groupId) {
      loadGroupMembers(groupId);
    } else {
      setMembers([]);
    }
  };

  const handleAddMember = (member: NewMember) => {
    setMembers([
      ...members,
      {
        id: `new-${Date.now()}`,
        name: member.name,
        isVegetarian: member.isVegetarian,
        drinksAlcohol: member.drinksAlcohol,
        isNew: true,
      },
    ]);
  };

  const handleRemoveMember = (memberId: string) => {
    setMembers(members.filter((m) => m.id !== memberId));
  };

  const handleFileSelected = (file: File) => {
    setSelectedFile(file);
    setStep('setup');
  };

  const handleUploadAndProcess = async () => {
    if (!selectedFile || !selectedGroup) return;

    setUploading(true);
    setStep('processing');

    try {
      // First, add any new members to the group
      for (const member of members.filter((m) => m.isNew)) {
        await groupsAPI.addMember(selectedGroup, {
          guestName: member.name,
          isVegetarian: member.isVegetarian,
          drinksAlcohol: member.drinksAlcohol,
        });
      }

      // Upload the bill
      const formData = new FormData();
      formData.append('bill', selectedFile);
      formData.append('groupId', selectedGroup);
      formData.append('splitMode', splitMode);

      const result = await billsAPI.upload(formData);
      navigate(`/bills/${result.bill.id}`);
    } catch (error) {
      console.error('Upload failed:', error);
      setStep('setup');
      alert('Failed to upload bill. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header
        title={step === 'upload' ? 'Scan Bill' : step === 'setup' ? 'Split Setup' : 'Processing'}
        showBack
        onBack={() => {
          if (step === 'setup') {
            setStep('upload');
            setSelectedFile(null);
          } else {
            navigate(-1);
          }
        }}
      />

      <main className="p-4 pb-8">
        {/* Progress Steps */}
        <div className="flex items-center gap-2 mb-6">
          <div className={`flex-1 h-1 rounded-full ${step !== 'upload' ? 'bg-green-500' : 'bg-green-500'}`} />
          <div className={`flex-1 h-1 rounded-full ${step === 'setup' || step === 'processing' ? 'bg-green-500' : 'bg-slate-200'}`} />
          <div className={`flex-1 h-1 rounded-full ${step === 'processing' ? 'bg-green-500' : 'bg-slate-200'}`} />
        </div>

        {step === 'upload' && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Upload your bill
              </h1>
              <p className="text-slate-500">
                Take a photo or choose from gallery
              </p>
            </div>

            <BillUploader onUpload={handleFileSelected} />

            <div className="text-center text-sm text-slate-500">
              <p>We'll scan the bill and extract all items automatically</p>
            </div>
          </div>
        )}

        {step === 'setup' && (
          <div className="space-y-6 animate-fade-in">
            {/* Selected Bill Preview */}
            {selectedFile && (
              <Card className="p-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{selectedFile.name}</p>
                    <p className="text-xs text-slate-500">Ready to scan</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedFile(null);
                      setStep('upload');
                    }}
                    className="p-2 rounded-lg hover:bg-slate-100"
                  >
                    <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </Card>
            )}

            {/* Select Group */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Group
              </label>
              <select
                value={selectedGroup}
                onChange={(e) => handleGroupChange(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              >
                <option value="">Choose a group...</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => navigate('/groups/new')}
                className="mt-2 text-sm text-green-600 font-medium"
              >
                + Create new group
              </button>
            </div>

            {/* Split Mode */}
            {selectedGroup && (
              <>
                <SplitModeSelector value={splitMode} onChange={setSplitMode} />

                {/* Members */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-slate-700">
                      People splitting ({members.length})
                    </label>
                    <button
                      onClick={() => setShowAddMember(true)}
                      className="text-sm text-green-600 font-medium flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add
                    </button>
                  </div>

                  <div className="space-y-2">
                    {members.map((member) => (
                      <Card key={member.id} padding="sm">
                        <div className="flex items-center gap-3">
                          <Avatar name={member.name} size="sm" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 text-sm truncate">
                              {member.name}
                              {member.isNew && (
                                <span className="ml-2 text-xs text-green-600">(new)</span>
                              )}
                            </p>
                            <div className="flex gap-1.5 mt-0.5">
                              {member.isVegetarian && (
                                <Badge variant="veg" size="sm">Veg</Badge>
                              )}
                              {member.drinksAlcohol && (
                                <Badge variant="alcohol" size="sm">Drinks</Badge>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </Card>
                    ))}
                  </div>

                  {members.length === 0 && (
                    <div className="text-center py-6 text-slate-500 text-sm">
                      No members yet. Add people to split the bill.
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  onClick={handleUploadAndProcess}
                  loading={uploading}
                  fullWidth
                  size="lg"
                  disabled={members.length < 2}
                >
                  Scan & Split Bill
                </Button>

                {members.length < 2 && (
                  <p className="text-center text-sm text-slate-500">
                    Add at least 2 people to split the bill
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {step === 'processing' && (
          <div className="flex flex-col items-center justify-center py-16 animate-fade-in">
            <div className="w-20 h-20 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-green-600 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Scanning your bill...</h2>
            <p className="text-slate-500 text-center max-w-xs">
              We're extracting items from your bill. This usually takes a few seconds.
            </p>
            <div className="mt-8 flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
      </main>

      <QuickAddMember
        isOpen={showAddMember}
        onClose={() => setShowAddMember(false)}
        onAdd={handleAddMember}
      />
    </div>
  );
};

export default ScanBill;
